import axios from 'axios';
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { wfsCache, debounce, bboxOverlap, requestManager } from '../../utils/wfsCache';
import Table from 'react-bootstrap/Table';
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { trackWfsQuery, timed } from '../../firebase/telemetry';
import { logError } from '../../firebase/errorReporting';
import {
    Popup,
    Source,
    Layer,
    useMap
} from 'react-map-gl/maplibre';

const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER,
    timeout: 30000
});

// Debounced query function
export const debouncedQueryDHWFS = debounce(queryDHWFSOptimized, 300);

/**
 * Optimized drill hole WFS query with caching and request management
 */
export function queryDHWFSOptimized(bboxString, setStateGeoJSON, dhFilters, retryCount = 0) {
    const cacheKey = wfsCache.generateKey(bboxString, dhFilters, 'drillholes');
    const startedAt = Date.now();

    // Check cache first
    const cachedResult = wfsCache.get(cacheKey);
    if (cachedResult) {
        // Only report a hit on the first attempt: a retry that lands on a
        // cache entry would otherwise inflate the hit rate.
        if (retryCount === 0) {
            trackWfsQuery({
                layer: 'drillholes',
                cacheHit: true,
                durationMs: Date.now() - startedAt,
                featureCount: cachedResult?.features?.length,
            });
        }
        setStateGeoJSON(cachedResult);
        return Promise.resolve(cachedResult);
    }

    // Create abort controller for this request
    const controller = requestManager.createController('drillholes');

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        and
        au_gpt BETWEEN ${dhFilters.gptmt} AND ${dhFilters.gptlt} 
        and todepth <= ${dhFilters.maxDepth}
    `;

    const maxRetries = 10;
    const baseDelay = 1000;

    return timed('wfs_drillholes', () => GoldFinderGeoserver.get("/ne/ows?", {
        signal: controller.signal,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_drill_holes',
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    })).then((response) => {
        // Process the data more efficiently with deduplication
        const features = response.data.features || [];
        const processedFeatures = new Map();
        
        // Use Map for O(1) lookups instead of array.find() for deduplication
        features.forEach(feature => {
            const id = feature.properties.dhgeochemid;
            if (processedFeatures.has(id)) {
                const existing = processedFeatures.get(id);
                existing.properties.au_gpt = +existing.properties.au_gpt + +feature.properties.au_gpt;
            } else {
                processedFeatures.set(id, feature);
            }
        });

        const result = {
            ...response.data,
            features: Array.from(processedFeatures.values())
        };

        trackWfsQuery({
            layer: 'drillholes',
            cacheHit: false,
            durationMs: Date.now() - startedAt,
            // Post-deduplication count: what actually reaches the map.
            featureCount: result.features.length,
        });
        // Cache the result
        wfsCache.set(cacheKey, result);
        setStateGeoJSON(result);
        return result;
    }).catch((error) => {
        const canceled = error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED';
        if (!canceled) {
            console.error('Drill hole WFS query error:', error);
            // Cancellations are routine (every map pan aborts in-flight
            // queries) — reporting them would drown the real failures.
            trackWfsQuery({
                layer: 'drillholes',
                cacheHit: false,
                durationMs: Date.now() - startedAt,
                featureCount: 0,
                status: error.code === 'ECONNABORTED' ? 'timeout' : 'error',
            });
        }

        // Don't re-throw canceled errors
        if (canceled) {
            return null;
        }

        // Retry on timeout or network errors
        if ((error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') && retryCount < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            console.log(`Retrying drill hole query in ${delay}ms (attempt ${retryCount + 1}/${maxRetries + 1})`);
            
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(queryDHWFSOptimized(bboxString, setStateGeoJSON, dhFilters, retryCount + 1));
                }, delay);
            });
        }
        
        // For other errors or max retries exceeded, return empty GeoJSON.
        // The UI silently shows zero drill holes here, which is
        // indistinguishable from an area that genuinely has none.
        logError(error, { component: 'DrillHoleOverlay', layer: 'drillholes', retryCount });

        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        
        setStateGeoJSON(emptyResult);
        return emptyResult;
    });
}

/**
 * Drill collars, served as vector tiles.
 *
 * Replaces a per-viewport WFS that returned every assay INTERVAL - 28,706,414
 * rows over 1,923,544 collars, 14.9 each, every one carrying the same collar
 * geometry. The map painted 14.9 dots on one coordinate. That was the stacking.
 *
 * The tile layer is one row per collar.
 */
export const DH_TILE_LAYER = 'ne:wa_au_drill_holes_tiles';
export const DH_TILE_SOURCE = 'dh-collars';
export const DH_TILE_SOURCE_LAYER = 'wa_au_drill_holes_tiles';
/** Click target: a wide invisible circle under the triangles. */
export const DH_TILE_BACKGROUND_LAYER = 'dh-tile-background';

/**
 * Collars are only drawn from here in.
 *
 * The tile thins by grade-thickness at low zoom because it has to - an
 * unfiltered z7 tile is 39.7 MB - so it holds every collar only from z11 up.
 * Drawing before that would mean a user who lowers their threshold sees nothing
 * new until they zoom further in.
 */
export const DH_POINT_MINZOOM = 11.3;

const dhGeoserverBase = (process.env.REACT_APP_GOLD_FINDER_GEOSERVER || '').replace(/\/$/, '');

export const dhTileUrl =
    `${dhGeoserverBase}/gwc/service/tms/1.0.0/` +
    `${encodeURIComponent(DH_TILE_LAYER)}@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf`;

/* ==========================================================================
 * HEAT
 *
 * Below DH_POINT_MINZOOM the drill layer drew NOTHING. The collar tiles thin by
 * grade at low zoom - an unfiltered z7 tile is 39.7 MB - and the DEFAULT
 * threshold of 0.1 g/t sits BELOW the z10 band floor of 1, so at the default
 * nothing qualified until the unfiltered z11 tile.
 *
 * Thinning is also the wrong answer at that range: drawing only the best holes
 * implies the rest of the ground was never drilled. The grid shows where
 * drilling HAPPENED as well as where it hit.
 * ========================================================================== */

export const DH_HEAT_LAYER = 'ne:wa_au_drill_holes_heat';
export const DH_HEAT_SOURCE = 'dh-heat';
export const DH_HEAT_SOURCE_LAYER = 'wa_au_drill_holes_heat';

export const dhHeatTileUrl =
    `${dhGeoserverBase}/gwc/service/tms/1.0.0/` +
    `${encodeURIComponent(DH_HEAT_LAYER)}@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf`;

/**
 * Cells the user's filter admits.
 *
 * EXACT for both dimensions the drill filter has, which the soil grid cannot
 * manage for its date range. max(auNN) over a cell is precisely "the best
 * result here at or above NN metres", so `auNN >= threshold` is precisely
 * "this cell holds at least one qualifying hole at that depth".
 *
 * `has` guards the null case exactly as the collar filter does: a cell where no
 * hole has an assay above the selected depth must DISAPPEAR rather than read as
 * zero and glow at the bottom of the ramp.
 *
 * The upper cap (gptlt) is deliberately not applied - a cell whose max is 30
 * may also hold holes at 0.5, and the cell carries nothing that could resolve
 * which, so faking it is worse than omitting it. The default is 9999.
 */
export function buildDhHeatFilter(dhFilters) {
    const col = dhDepthColumn(dhFilters.maxDepth);
    return ['all', ['has', col], ['>=', ['to-number', ['get', col]], dhFilters.gptmt]];
}

/**
 * Heat paint for a given filter.
 *
 * WEIGHT IS CAPPED AT 30 g/t, and the cap is load-bearing rather than cosmetic.
 * The grid's maximum au20 is 1,125,513 g/t - over 100% gold, so physically
 * impossible - and 40 cells sit above 10,000. An uncapped linear ramp would
 * scale every real anomaly in the state against a data-entry error and render
 * the map uniformly cold.
 *
 * The stops come from the measured spread over the 73,232 fine cells carrying
 * an au20: >=0.1 in 14,313, >=1 in 7,082, >=2.5 in 5,256, >=10 in 3,034,
 * >=30 in 1,701. So 30 is the genuine top of the useful range.
 */
export function dhHeatmapPaintFor(dhFilters) {
    const col = dhDepthColumn(dhFilters.maxDepth);
    const v = ['to-number', ['coalesce', ['get', col], 0]];
    return {
        'heatmap-weight': ['interpolate', ['linear'], v,
            0, 0, 0.1, 0.15, 1, 0.4, 2.5, 0.6, 10, 0.85, 30, 1],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 2, 1.4, 7, 1.0, 10, 0.7],
        // Radius must stay above the cell pitch or the aggregation shows as
        // itself - separate blobs rather than a surface. Pitch DOUBLES every
        // zoom level and RESETS at z7, where the server switches from the
        // 0.1 degree grid to the 0.01 degree one; hence the discontinuity.
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'],
            2, 10, 4, 10, 5, 12, 6, 20, 6.99, 36,   // 0.1 degree grid
            7, 10, 8, 12, 9, 18, 10, 30, 10.6, 40], // 0.01 degree grid
        'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(0,0,0,0)',
            0.15, 'rgba(0,0,255,0.5)',
            0.4, '#FFA500',
            0.65, '#FF0000',
            1, '#800080',
        ],
        // Reaches zero exactly at DH_POINT_MINZOOM, where the collars take
        // over, so the two layers are exact complements and heat is never drawn
        // over collars that are already there.
        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'],
            DH_POINT_MINZOOM - 1.8, 0.85, DH_POINT_MINZOOM, 0],
    };
}

/**
 * The assay intervals behind one collar.
 *
 * The tile carries one row per collar with only the precomputed best result per
 * depth cutoff, so the actual intervals - their depths and grades - are not in
 * it and have to be fetched. Same pattern the soil popup uses.
 *
 * Filtered on `collar_id`, which is indexed (idx_au_drill_holes_collar_id).
 * Ordered by depth so the popup can show the hole top-down.
 */
export function queryDrillIntervalsByCollar(collarId) {
    return GoldFinderGeoserver.get("/ne/ows?", {
        params: {
            service: 'WFS', version: '1.1.0', request: 'GetFeature',
            typeNames: 'ne:wa_au_drill_holes', maxFeatures: 200,
            outputFormat: 'application/json', srs: 'EPSG:4326',
            sortBy: 'fromdepth',
            cql_filter: `collar_id = ${Number(collarId)}`,
        },
    });
}

/**
 * Which precomputed depth-cutoff column answers this filter.
 *
 * The collar view stores the best result at 5 m steps - au05 through au50 -
 * because the winning interval MOVES with the depth slider and cannot be
 * recomputed from a tile holding one row per collar. Anything between steps is
 * rounded DOWN to the cutoff that is actually available, so the map never
 * claims to have looked deeper than it did.
 *
 * The UI slider steps in 5s to match. If it ever stops matching, this rounding
 * is what keeps the answer honest rather than approximate-but-silent.
 */
export function dhDepthColumn(maxDepth) {
    const d = Math.max(5, Math.min(50, Math.floor((Number(maxDepth) || 0) / 5) * 5));
    return `au${String(d).padStart(2, '0')}`;
}

/**
 * The best Au grade, in g/t, at the selected depth.
 *
 * Grade-thickness was tried here and reverted. It picked a different interval
 * than max grade for only 2.9% of collars - grades span orders of magnitude
 * while interval lengths span about 5x, so grade dominates the product - while
 * changing the unit for 100% of them, forcing a palette rescale and quietly
 * redefining every saved preset. Grade-thickness belongs in the popup, where
 * "4 m @ 3.2 g/t = 12.8 g/t.m" can be stated without ambiguity; the popup
 * already fetches the real intervals.
 */
export const dhValue = (maxDepth) => ['to-number', ['coalesce', ['get', dhDepthColumn(maxDepth)], 0]];

/**
 * The client-side equivalent of the CQL the WFS used to send.
 *
 * `has` guards the null case: a collar with no assay above the selected depth
 * has a null column and must disappear, not be treated as zero and drawn.
 */
export function buildDhTileFilter(dhFilters) {
    const col = dhDepthColumn(dhFilters.maxDepth);
    const v = dhValue(dhFilters.maxDepth);
    return ['all',
        ['has', col],
        ['>=', v, dhFilters.gptmt],
        ['<=', v, dhFilters.gptlt],
    ];
}

/* ==========================================================================
 * COLLAR POPUP
 *
 * A click used to open `createSingleDrillHolePopupTable`, which reads the
 * fields of one assay INTERVAL - au_gpt, fromdepth, todepth, dhassayid,
 * companysampleid. None of those exist on a collar tile. `au_gpt.toFixed(2)`
 * on undefined threw inside render, so the popup never appeared and the click
 * did nothing visible at all. That popup is still right for the global search,
 * which returns interval features off `ne:wa_au_drill_holes`; it is wrong for
 * the map, and the map now has this instead.
 * ========================================================================== */

/** Reported ids come back null when absent, and 0 is a legal id. */
const isReported = (v) => v != null;

/** The cutoff, in metres, that `dhDepthColumn` actually answered with. */
export function dhDepthCutoff(maxDepth) {
    return Number(dhDepthColumn(maxDepth).slice(2));
}

const fmtGpt = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2));

/**
 * Grade x thickness for one interval, in g/t.m.
 *
 * Stated here rather than used for colouring: the map is coloured by grade
 * (see `dhValue`), and this is the number that says whether a grade is worth
 * anything - "4 m @ 3.2 g/t = 12.8 g/t.m". Intervals with a non-positive
 * length exist (0.1% of rows), hence the clamp.
 */
function gradeThickness(p) {
    if (p.au_gpt == null) return null;
    const len = Math.max(Number(p.todepth) - Number(p.fromdepth), 0);
    if (!Number.isFinite(len)) return null;
    return Number(p.au_gpt) * len;
}

/**
 * One collar, then the intervals behind it.
 *
 * The tile carries only companyholeid, anumber, holetype, hole_maxdepth,
 * n_intervals and the precomputed best grade at each 5 m cutoff (au05..au50),
 * so the intervals are fetched on open.
 *
 * `collar_id` is the MVT FEATURE ID, not an attribute - GeoServer takes it
 * from the view's unique index and omits it from the tags - so it is read off
 * `feature.id`, and a feature without one degrades to the collar summary
 * rather than firing a query for `collar_id = undefined`.
 */
function DrillCollarPopupBody({ collar, maxDepth, setModalShow, setReportedAssay }) {
    const props = collar.properties || {};
    const collarId = collar.id;
    const cutoff = dhDepthCutoff(maxDepth);
    const best = props[dhDepthColumn(maxDepth)];

    const [intervals, setIntervals] = useState({ status: 'loading', rows: [] });

    useEffect(() => {
        if (collarId === undefined || collarId === null) {
            setIntervals({ status: 'unavailable', rows: [] });
            return undefined;
        }
        let cancelled = false;
        setIntervals({ status: 'loading', rows: [] });
        queryDrillIntervalsByCollar(collarId)
            .then(res => {
                if (cancelled) return;
                const rows = (res?.data?.features || [])
                    .map(f => f.properties)
                    .sort((a, b) => (a.fromdepth ?? 0) - (b.fromdepth ?? 0));
                setIntervals({ status: 'ok', rows });
            })
            .catch(err => {
                if (cancelled) return;
                console.warn('drill interval lookup failed', err);
                logError(err, { component: 'DrillCollarPopup', action: 'query_intervals' });
                setIntervals({ status: 'error', rows: [] });
            });
        return () => { cancelled = true; };
    }, [collarId]);

    // The A-Number flag rides on the interval rows, so it can only be known
    // once they arrive - the tile has no reporting columns.
    const anumberReported = intervals.rows.some(r => isReported(r.reported_sample_anumber_id));

    return (
        <>
            <Table striped bordered className="mb-1">
                <tbody>
                    <tr>
                        <td>Hole:</td>
                        <td>{props.companyholeid || (collarId != null ? `Collar ${collarId}` : '—')}</td>
                    </tr>
                    <tr>
                        <td>A Number:</td>
                        <td>
                            <a target="_blank" rel="noreferrer"
                               href={`https://wamex.dmp.wa.gov.au/Wamex/Search/ReportDetails?ANumber=${props.anumber}`}>
                                {props.anumber}
                            </a>
                            {anumberReported
                                ? <FontAwesomeIcon title="The samples related to this A Number have been reported as inaccurate or incorrect." style={{ marginLeft: 3 }} icon={faCircleExclamation} />
                                : ''}
                        </td>
                    </tr>
                    <tr>
                        <td>Hole Type:</td>
                        <td>{props.holetype || '—'}</td>
                    </tr>
                    <tr>
                        <td>Max Depth (Hole):</td>
                        <td>{props.hole_maxdepth != null ? `${props.hole_maxdepth} m` : '—'}</td>
                    </tr>
                    <tr>
                        {/* This is the number the mark is coloured by, and it
                            moves with the depth slider, so name the cutoff. */}
                        <td>{`Best Au to ${cutoff} m:`}</td>
                        <td>{fmtGpt(best)} g/t</td>
                    </tr>
                    <tr>
                        <td>Assayed Intervals:</td>
                        <td>{props.n_intervals ?? '—'}</td>
                    </tr>
                </tbody>
            </Table>

            {intervals.status === 'loading' && (
                <div className="text-muted" style={{ fontSize: 11 }}>Loading intervals…</div>
            )}
            {intervals.status === 'error' && (
                <div className="text-danger" style={{ fontSize: 11 }}>Could not load this hole&rsquo;s intervals.</div>
            )}
            {intervals.status === 'unavailable' && (
                <div className="text-muted" style={{ fontSize: 11 }}>
                    This mark carries no collar id, so its intervals cannot be looked up.
                </div>
            )}
            {intervals.status === 'ok' && intervals.rows.length === 0 && (
                <div className="text-muted" style={{ fontSize: 11 }}>No intervals recorded for this hole.</div>
            )}
            {intervals.status === 'ok' && intervals.rows.length > 0 && (
                <>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <Table striped bordered size="sm" className="mb-1" style={{ fontSize: 11 }}>
                            <thead>
                                <tr>
                                    <th>From</th><th>To</th><th>Au g/t</th><th>g/t&middot;m</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {intervals.rows.map(r => {
                                    const gt = gradeThickness(r);
                                    const reported = isReported(r.reported_sample_id)
                                        || isReported(r.reported_sample_anumber_id);
                                    // Below the cutoff the map value ignores
                                    // this row. Showing it anyway is right -
                                    // the hole is the hole - but it must not
                                    // read as part of the headline number.
                                    const belowCutoff = Number(r.todepth) > cutoff;
                                    return (
                                        <tr key={r.id ?? `${r.fromdepth}-${r.todepth}`}
                                            style={belowCutoff ? { opacity: 0.55 } : undefined}>
                                            <td>{r.fromdepth}</td>
                                            <td>{r.todepth}</td>
                                            <td>{fmtGpt(r.au_gpt)}</td>
                                            <td>{gt == null ? '—' : gt.toFixed(2)}</td>
                                            <td>
                                                {reported
                                                    ? <FontAwesomeIcon title="This sample has been reported as inaccurate or incorrect." icon={faCircleExclamation} />
                                                    : <button className="btn btn-link btn-sm p-0" style={{ fontSize: 10 }}
                                                        onClick={() => { setModalShow(true); setReportedAssay(r); }}>Report</button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                    {intervals.rows.some(r => Number(r.todepth) > cutoff) && (
                        <div className="text-muted" style={{ fontSize: 10 }}>
                            Greyed rows are below the {cutoff} m cutoff and are not in the value above.
                        </div>
                    )}
                </>
            )}
        </>
    );
}

/**
 * Popup for a collar clicked on the map.
 *
 * Same signature as `createSingleDrillHolePopupTable` plus the depth filter,
 * which the collar value depends on - without it the popup would have to guess
 * which of au05..au50 the map drew.
 */
export function createSingleCollarPopupTable(collar, lngLat, setClosed, setModalShow, setReportedAssay, maxDepth) {
    return <Popup latitude={lngLat.lat} longitude={lngLat.lng}
        anchor="bottom"
        maxWidth="420px"
        onClose={() => setClosed(false)}>
        <DrillCollarPopupBody collar={collar} maxDepth={maxDepth}
            setModalShow={setModalShow} setReportedAssay={setReportedAssay} />
    </Popup>;
}

/**
 * Optimized AssayLayers component with memoization
 */
function AssayLayersOptimized({ assayMarkerColourSettings, overlayVisibility, drillHoleFilters }) {
    const orderedAssayMarkerArray = useMemo(() => 
        assayMarkerColourSettings.toSorted((a, b) => a.moreThan - b.moreThan),
        [assayMarkerColourSettings]
    );
    const userFilter = useMemo(() => buildDhTileFilter(drillHoleFilters), [drillHoleFilters]);
    const value = useMemo(() => dhValue(drillHoleFilters.maxDepth), [drillHoleFilters.maxDepth]);

    const layers = useMemo(() => {
        const layerConfigs = [];

        for (let i = 0; i < orderedAssayMarkerArray.length; i++) {
            const next = i + 1;
            const config = orderedAssayMarkerArray[i];
            const layerId = config.id + '-dh';

            const layerConfig = {
                id: layerId,
                source: DH_TILE_SOURCE,
                'source-layer': DH_TILE_SOURCE_LAYER,
                type: 'symbol',
                minzoom: DH_POINT_MINZOOM,
                layout: { 
                    visibility: `${overlayVisibility.dh ? "visible" : "none"}`,
                    'icon-image': `triangle-${config.id}`,
                    'icon-size': 1.2,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'icon-anchor': 'center'
                },
                paint: {
                    'icon-opacity': 1.0
                },
                // Colour band AND the user's filters - the server no longer
                // applies them, a tile being the same for everybody. The value
                // is the grade-thickness at the SELECTED depth, so changing the
                // depth slider rebinds every band to a different column.
                filter: ['all',
                    userFilter,
                    ['>=', value, config.moreThan],
                    ['<', value, i !== orderedAssayMarkerArray.length - 1 ? orderedAssayMarkerArray[next].moreThan : 9999]
                ]
            };

            layerConfigs.push(layerConfig);
        }

        return layerConfigs;
    }, [orderedAssayMarkerArray, overlayVisibility.dh, userFilter, value]);

    return (
        <>
            {layers.map((layerConfig) => (
                <Layer key={layerConfig.id} {...layerConfig} />
            ))}
        </>
    );
}

/**
 * Optimized DrillHoleOverlay component with performance improvements
 */
export function DrillHoleOverlayMapLibreOptimized({ 
    overlayVisibility, 
    drillHoleMarkerColourSettings, 
    drillHoleGeoJson, 
    setDrillHoleGeoJson, 
    drillHoleFilters 
}) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "drillhole_samples",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } },
        "features": []
    };

    const { goldfindermap } = useMap();
    const lastBboxRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // Optimized triangle image creation with memoization
    const createTriangleImages = useCallback(() => {
        if (!goldfindermap || !drillHoleMarkerColourSettings) return;
        
        drillHoleMarkerColourSettings.forEach((setting) => {
            const canvas = document.createElement('canvas');
            canvas.width = 24;
            canvas.height = 24;
            const ctx = canvas.getContext('2d');
            
            // Clear canvas with transparent background
            ctx.clearRect(0, 0, 24, 24);
            
            // Draw white border triangle first (slightly larger)
            ctx.beginPath();
            ctx.moveTo(12, 20); // bottom point
            ctx.lineTo(4, 4);   // top left
            ctx.lineTo(20, 4);  // top right
            ctx.closePath();
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            
            // Draw colored triangle on top
            ctx.beginPath();
            ctx.moveTo(12, 19); // bottom point
            ctx.lineTo(5, 5);   // top left
            ctx.lineTo(19, 5);  // top right
            ctx.closePath();
            ctx.fillStyle = setting.color;
            ctx.fill();
            
            const imageId = `triangle-${setting.id}`;
            
            // Convert canvas to PNG data URL
            const dataURL = canvas.toDataURL('image/png');
            const img = new Image();
            img.onload = () => {
                // Remove existing image if it exists
                if (goldfindermap.hasImage(imageId)) {
                    goldfindermap.removeImage(imageId);
                }
                goldfindermap.addImage(imageId, img);
            };
            img.src = dataURL;
        });
    }, [goldfindermap, drillHoleMarkerColourSettings]);

    // Create triangle images when map or settings change
    useEffect(() => {
        createTriangleImages();
        
        // Cleanup function
        return () => {
            // hasImage reaches through the style to the image manager, so it
            // THROWS rather than returning false once the map has been torn
            // down - and this cleanup runs on exactly that path. Guarded so an
            // unmount cannot take the whole app down with it.
            try {
                if (!goldfindermap?.getStyle?.()) return;
            } catch {
                return;
            }
            if (goldfindermap && drillHoleMarkerColourSettings) {
                drillHoleMarkerColourSettings.forEach((setting) => {
                    const imageId = `triangle-${setting.id}`;
                    try {
                        if (goldfindermap.hasImage(imageId)) {
                            goldfindermap.removeImage(imageId);
                        }
                    } catch { /* style went away mid-cleanup */ }
                });
            }
        };
    }, [createTriangleImages]);

    // Optimized query function
    const queryDrillHoles = useCallback(async (bbox) => {
        if (!goldfindermap || goldfindermap.getZoom() <= 10.6 || !overlayVisibility.dh) return;

        // Check if bbox has changed significantly to avoid redundant queries
        if (lastBboxRef.current && bboxOverlap(bbox, lastBboxRef.current)) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await debouncedQueryDHWFS(bbox, setDrillHoleGeoJson, drillHoleFilters);
            if (result !== null) {
                lastBboxRef.current = bbox;
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                console.error('Error querying drill holes:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [goldfindermap, drillHoleFilters, setDrillHoleGeoJson, overlayVisibility.dh]);

    // Effect for filter changes
    useEffect(() => {
        if (!goldfindermap) return;
        
        if (!overlayVisibility.dh) {
            // Clear data when overlay is disabled
            setDrillHoleGeoJson(emptyGeoJson);
            return;
        }
        
        // Clear cache when filters change
        wfsCache.clearLayer('drillholes');
        // The bbox guard in the query above is a VIEWPORT dedupe and knows
        // nothing about filters: it skips any fetch whose bbox overlaps the
        // last one by more than 80%. On a filter change the bbox is identical,
        // so it overlaps 100% and the fetch was skipped every time - the map
        // kept showing the old filter's results until you panned far enough to
        // beat the threshold. Forgetting the last bbox is the same statement
        // as clearing the cache on the line above: what we fetched before was
        // for different filters and does not count.
        lastBboxRef.current = null;
        
        if (goldfindermap.getZoom() > 10.6) {
            const bounds = goldfindermap.getBounds();
            const wfsbbox = [
                bounds.getSouthWest().lng, 
                bounds.getSouthWest().lat, 
                bounds.getNorthEast().lng, 
                bounds.getNorthEast().lat
            ].join();
            
            queryDrillHoles(wfsbbox);
        }
    }, [drillHoleFilters, queryDrillHoles, overlayVisibility.dh]);

    // Map movement effect
    useEffect(() => {
        if (!goldfindermap) return;

        const handleMapUpdate = () => {
            if (goldfindermap.getZoom() > 10.6 && overlayVisibility.dh) {
                const bounds = goldfindermap.getBounds();
                const wfsbbox = [
                    bounds.getSouthWest().lng, 
                    bounds.getSouthWest().lat, 
                    bounds.getNorthEast().lng, 
                    bounds.getNorthEast().lat
                ].join();
                queryDrillHoles(wfsbbox);
            } else {
                // Clear data when zoomed out
                setDrillHoleGeoJson(emptyGeoJson);
            }
        };

        // Initial load
        handleMapUpdate();

        // Listen for map events with debouncing
        const debouncedUpdate = debounce(handleMapUpdate, 500);
        goldfindermap.on('moveend', debouncedUpdate);
        goldfindermap.on('zoomend', handleMapUpdate);

        return () => {
            goldfindermap.off('moveend', debouncedUpdate);
            goldfindermap.off('zoomend', handleMapUpdate);
        };
    }, [goldfindermap, queryDrillHoles, setDrillHoleGeoJson, overlayVisibility.dh]);

    // Click target and halo under the triangles. The only drill layer in
    // interactiveLayerIds, so a click resolves here rather than depending on
    // which colour band the collar fell into.
    const backgroundConfig = useMemo(() => ({
        id: DH_TILE_BACKGROUND_LAYER,
        source: DH_TILE_SOURCE,
        'source-layer': DH_TILE_SOURCE_LAYER,
        type: 'circle',
        minzoom: DH_POINT_MINZOOM,
        paint: { 'circle-radius': 8, 'circle-color': '#000000', 'circle-opacity': 0 },
        // Must carry the same filter as the triangles, or filtered-out collars
        // stay invisibly clickable.
        filter: buildDhTileFilter(drillHoleFilters),
        layout: { visibility: `${overlayVisibility.dh ? "visible" : "none"}` }
    }), [overlayVisibility.dh, drillHoleFilters]);

    // Zoomed out, collars are drawn as a heatmap off a pre-aggregated grid.
    //
    // The opacity ramp reaches zero at exactly DH_POINT_MINZOOM, so the layer
    // can stop there too - MapLibre hides a layer at zoom >= maxzoom and shows
    // one at zoom >= minzoom, which makes this and the triangle layers exact
    // complements with nothing cut off mid-fade.
    const heatConfig = useMemo(() => ({
        id: 'dh-heat-layer',
        source: DH_HEAT_SOURCE,
        'source-layer': DH_HEAT_SOURCE_LAYER,
        type: 'heatmap',
        maxzoom: DH_POINT_MINZOOM,
        filter: buildDhHeatFilter(drillHoleFilters),
        paint: dhHeatmapPaintFor(drillHoleFilters),
        layout: { visibility: `${overlayVisibility.dh ? "visible" : "none"}` }
    }), [overlayVisibility.dh, drillHoleFilters]);

    return (
        overlayVisibility.dh && (
            <>
                {/*
                  * Pre-aggregated, so panning at low zoom costs the database
                  * nothing and this needs no bbox tracking, debounce or abort
                  * controller. Measured on prod: 264 KB at z3, 130 KB at z7 -
                  * a tenth of the soil grid, because there are far fewer
                  * collars and they cluster harder.
                  */}
                <Source id={DH_HEAT_SOURCE} type="vector" scheme="tms"
                        tiles={[dhHeatTileUrl]} minzoom={1} maxzoom={11}>
                    {/*
                      * Below anchor-overlay-top for the same reason as the soil
                      * heat: above the base imagery but UNDER every vector
                      * overlay. Appended on mount it would land on top of
                      * tenements, which draw from z8, and a wash at up to 0.85
                      * opacity over their fills reads as them having vanished.
                      */}
                    <Layer {...heatConfig} beforeId='anchor-overlay-top' />
                </Source>
                <Source id={DH_TILE_SOURCE} type="vector" scheme="tms"
                        tiles={[dhTileUrl]} minzoom={7} maxzoom={16}>
                    <Layer {...backgroundConfig} />
                    <AssayLayersOptimized 
                        overlayVisibility={overlayVisibility} 
                        assayMarkerColourSettings={drillHoleMarkerColourSettings}
                        drillHoleFilters={drillHoleFilters}
                    />
                </Source>
            </>
        )
    );
}